import { memo, useCallback, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  useParametricSelector,
  useProfileSelector,
} from "../../../hooks/useParametricSelector";
import { getLoadedUserByUsername } from "../../../redux/slices/dataSlice";
import { useSelector, useDispatch } from "react-redux";
import LoadingScreen from "../../LoadingScreen";
import {
  LoginState,
  getAccountState,
} from "../../../redux/slices/accountSlice";
import MarkitSecuredContainer from "../../../components/UserInfoContainers/MarkitSecuredContainer";
import { useTheme } from "../../../hooks/useTheme";
import {
  FollowType,
  FormAnswersV2,
  MembershipPlan,
  MembershipState,
  NotificationType,
  SavedFormQuestion,
  VerificationState,
} from "@markit/common.types";
import {
  checkIfFollowerIsSubscribed,
  checkIfLoggedInPhoneNumber,
  checkIfUserIsSubscribed,
  checkIfUserIsUnsubscribed,
  checkIfUserIsUnsubscribedMarkit,
  fetchFollowerDataFromPhoneNumber,
} from "../../../utils/userUtils";
import {
  finalizeFormFileUploadAnswers,
  getProfileFormQuestions,
  getUsersFormResponses,
  validateEventForm,
} from "../../../utils/eventUtils/formUtils";
import { createEmptyFormAnswersV2 } from "@markit/common.utils";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useNavigate } from "react-router-dom";
import {
  onNavigateRedirectPath,
  onProfilePageNavigatePath,
  scrollToTop,
} from "../../../utils/navigationUtils";
import SubscribeFlowInitial from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowInitial";
import SubscribeFlowQuestions from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowQuestions";
import SubscribeFlowMembership from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembership";
import SubscribeFlowMembershipCheckout from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembershipCheckout";
import { followUser } from "../../../utils/FollowUser";
import AlertContainer from "../../../components/Containers/AlertContainer";
import { useLoadUserByUsername } from "../../../hooks/useLoadUserByUsername";
import { FormResponsesProvider } from "../../../components/Form/FormResponsesContext";

export enum SubscribingStage {
  INITIAL = "Initial",
  PROFILE_QUESTIONS = "Profile Questions",
  MEMBERSHIP = "Membership",
  MEMBERSHIP_CHECKOUT = "Membership Checkout",
}

const ProfilePageSubscribe = memo(function ProfilePageSubscribeFn() {
  const { accountData, loggedIn, redirectPath } =
    useSelector(getAccountState).account;
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tempFullName, setTempFullName] = useState("");
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [verificationState, setVerificationState] = useState(
    VerificationState.UNVERIFIED
  );
  const [uid, setUid] = useState("");
  const [answers, setAnswers] = useState<FormAnswersV2>(
    createEmptyFormAnswersV2([], [])
  );
  const [formErrorIds, setFormErrorIds] = useState<string[]>([]);
  const [formFileUploads, setFormFileUploads] = useState<
    Record<string, string>
  >({});
  const [profileFormQuestions, setProfileFormQuestions] = useState<
    SavedFormQuestion[]
  >([]);
  const [selectedMembershipPlan, setSelectedMembershipPlan] =
    useState<MembershipPlan>();
  const [subscribingStage, setSubscribingStage] = useState<SubscribingStage>(
    SubscribingStage.INITIAL
  );
  const [processing, setProcessing] = useState(false);
  const [optedOut, setOptedOut] = useState(false);
  const [optedOutCreator, setOptedOutCreator] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [alertText, setAlertText] = useState({
    header: "",
    subHeader: "",
  });

  const username = useProfileSelector();
  useLoadUserByUsername(username);

  const {
    isLoading: isLoadingUserData,
    data: creatorData,
    isError: isErrorUserData,
  } = useParametricSelector(getLoadedUserByUsername, username);

  const isLoggedIn = useMemo(
    () => loggedIn === LoginState.LOGGED_IN,
    [loggedIn]
  );

  // show all non-hidden membership plans
  const visibleMembershipPlans = useMemo(
    () =>
      creatorData
        ? creatorData.membership.membershipPlans.filter((plan) => !plan.hidden)
        : [],
    [creatorData]
  );

  const membershipsEnabled = useMemo(
    () =>
      creatorData && creatorData?.membership.state === MembershipState.ACTIVE,
    [creatorData]
  );

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (_deps, isLatest) => {
      if (creatorData && initialLoading && isLatest()) {
        if (isLoggedIn) {
          // Check if user is logged in and already subscribed to creator
          const isSubscribed = await checkIfUserIsSubscribed(
            accountData.uid,
            creatorData.uid
          );
          if (isSubscribed) {
            onProfilePageNavigatePath(
              navigate,
              creatorData.username,
              creatorData.membership.state === MembershipState.ACTIVE
                ? "manage"
                : undefined
            );
            showNotificationBanner(
              dispatch,
              "Already Subscribed!",
              NotificationType.AFFIRMATIVE
            );
            setInitialLoading(false);
            return;
          }
        }

        // fetch the event form questions
        const formQuestions = await getProfileFormQuestions(creatorData.uid);
        setProfileFormQuestions(formQuestions);

        // Default the membership plan to the first paid option, if applicable
        if (membershipsEnabled) {
          const paidMembershipPlans = visibleMembershipPlans.filter(
            (membership) => membership.price !== 0
          );
          if (paidMembershipPlans.length > 0) {
            setSelectedMembershipPlan(paidMembershipPlans[0]);
          }
        }

        if (checkIfLoggedInPhoneNumber(loggedIn, accountData.phoneNumber)) {
          setTempFullName(accountData.fullName);
          setTempPhoneNumber(accountData.phoneNumber);
          setUid(accountData.uid);
          setVerificationState(VerificationState.VERIFIED);

          const [
            isUnsubscribedMarkit,
            isUnsubscribed,
            { formResponses, fileNameRecords },
          ] = await Promise.all([
            checkIfUserIsUnsubscribedMarkit(accountData.phoneNumber),
            checkIfUserIsUnsubscribed(accountData.uid, creatorData.uid),
            getUsersFormResponses(
              creatorData.uid,
              formQuestions,
              accountData.uid
            ),
          ]);
          // Check if logged in phone number is unsubscribed from markit number or creator
          // Even if using firebase OTP, we need them opted in so they can get confirmation texts
          setOptedOut(isUnsubscribedMarkit);
          setOptedOutCreator(isUnsubscribed);
          // set already answered form questions
          setAnswers(createEmptyFormAnswersV2(formQuestions, formResponses));
          setFormFileUploads(fileNameRecords);
        }

        setInitialLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatorData?.uid, loggedIn]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // Autofill any form inputs if user is not logged in and has verified their number
    // The logged in case is handled by the useAsyncOnMount above
    if (
      !initialLoading &&
      verificationState === VerificationState.VERIFIED &&
      creatorData
    ) {
      // set already answered form questions
      const { formResponses, fileNameRecords } = await getUsersFormResponses(
        creatorData.uid,
        profileFormQuestions,
        uid
      );
      setAnswers(createEmptyFormAnswersV2(profileFormQuestions, formResponses));
      setFormFileUploads(fileNameRecords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationState]);

  const followUserAccount = useCallback(
    async (answersToSubmit: FormAnswersV2) => {
      if (!creatorData) {
        return;
      }

      await followUser({
        userPhone: tempPhoneNumber,
        userFullName: tempFullName,
        userToFollow: creatorData,
        uid: "",
        answers: answersToSubmit,
        followType: FollowType.PROFILE,
      });

      if (membershipsEnabled) {
        scrollToTop();
        setSubscribingStage(SubscribingStage.MEMBERSHIP);
      } else {
        setProcessing(false);
        if (redirectPath) {
          onNavigateRedirectPath(navigate, dispatch, redirectPath);
        } else {
          onProfilePageNavigatePath(navigate, creatorData.username);
        }
        showNotificationBanner(
          dispatch,
          "Subscribed",
          NotificationType.AFFIRMATIVE
        );
      }
    },
    [
      creatorData,
      dispatch,
      membershipsEnabled,
      navigate,
      redirectPath,
      tempFullName,
      tempPhoneNumber,
    ]
  );

  /**
   * When the user verifies their number and presses the Subscribe button, three cases:
   * 1. Logged in user is already following creator
   *    a. Creator has membership plans redirect to profile manage subscription
   *    b. Creator has no membership plans, redirect to profile page
   * 2. Not following, has profile questions, show the questions to answer before subscribing
   * 3. a. Not following, has no profile questions AND no membership plans, subscribe the user with message
   *    b. Not following, has no profile questions but has membership plans, subscribe user and then go to plans
   */
  const verifyNumberOnPress = useCallback(async () => {
    if (!creatorData) {
      return;
    }
    setProcessing(true);
    const follower = await fetchFollowerDataFromPhoneNumber(
      creatorData.uid,
      tempPhoneNumber
    );
    if (follower && checkIfFollowerIsSubscribed(follower.status)) {
      if (redirectPath) {
        onNavigateRedirectPath(navigate, dispatch, redirectPath);
      } else {
        onProfilePageNavigatePath(
          navigate,
          creatorData.username,
          membershipsEnabled ? "manage" : undefined,
          true
        );
      }
      showNotificationBanner(
        dispatch,
        "Already Subscribed!",
        NotificationType.AFFIRMATIVE
      );
    } else if (profileFormQuestions.length > 0) {
      setSubscribingStage(SubscribingStage.PROFILE_QUESTIONS);
      scrollToTop();
      setProcessing(false);
    } else {
      await followUserAccount(answers);
    }
  }, [
    answers,
    creatorData,
    dispatch,
    followUserAccount,
    membershipsEnabled,
    navigate,
    profileFormQuestions.length,
    redirectPath,
    tempPhoneNumber,
  ]);

  const submitQuestionsOnPress = useCallback(async () => {
    if (!creatorData) {
      return;
    }
    setProcessing(true);

    const errorIds = validateEventForm(answers, profileFormQuestions);
    if (errorIds.length > 0) {
      setFormErrorIds(errorIds);
      setAlertText({
        header: "Please answer the required questions.",
        subHeader: "",
      });
      setProcessing(false);
      return;
    }

    let currentAnswers = answers;
    try {
      currentAnswers = await finalizeFormFileUploadAnswers({
        creatorUid: creatorData.uid,
        answers: currentAnswers,
        formQuestions: profileFormQuestions,
        fileNamesByQuestionId: formFileUploads,
        setAnswers,
        eventId: "",
        followerUid: uid,
      });
    } catch (e: any) {
      setProcessing(false);
      alert("Failed to upload a file. Please try again.");
      return;
    }

    await followUserAccount(currentAnswers);
  }, [
    answers,
    creatorData,
    formFileUploads,
    followUserAccount,
    profileFormQuestions,
    uid,
  ]);

  const chooseMembershipOnPress = useCallback(
    async (selectedMembership: MembershipPlan) => {
      if (!creatorData) {
        return;
      }
      setSelectedMembershipPlan(selectedMembership);
      if (selectedMembership.id === "free") {
        if (redirectPath) {
          onNavigateRedirectPath(navigate, dispatch, redirectPath);
        } else {
          onProfilePageNavigatePath(navigate, creatorData.username);
        }
        showNotificationBanner(
          dispatch,
          "Subscribed",
          NotificationType.AFFIRMATIVE
        );
      } else {
        setSubscribingStage(SubscribingStage.MEMBERSHIP_CHECKOUT);
        scrollToTop();
      }
    },
    [creatorData, dispatch, navigate, redirectPath]
  );

  if (isLoadingUserData || initialLoading || !creatorData) {
    return (
      <LoadingScreen
        isLoadingPage={isLoadingUserData || initialLoading || !creatorData}
        error={isErrorUserData}
      />
    );
  }

  const title = `${creatorData.fullName} (@${creatorData.username}) on Markit`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="og:description" content={creatorData.username} />
      </Helmet>
      <div
        className="ColumnNormalSpaced"
        style={{
          ...theme.SecondaryBG,
          minHeight: "100vh",
          position: "relative",
          paddingTop: 70,
        }}
      >
        {subscribingStage === SubscribingStage.INITIAL ? (
          <SubscribeFlowInitial
            creatorData={creatorData}
            processing={processing}
            tempFullName={tempFullName}
            setTempFullName={setTempFullName}
            tempPhoneNumber={tempPhoneNumber}
            setTempPhoneNumber={setTempPhoneNumber}
            verificationState={verificationState}
            setVerificationState={setVerificationState}
            optedOut={optedOut}
            setOptedOut={setOptedOut}
            optedOutCreator={optedOutCreator}
            setOptedOutCreator={setOptedOutCreator}
            verifyOnPress={verifyNumberOnPress}
            setUid={setUid}
          />
        ) : subscribingStage === SubscribingStage.PROFILE_QUESTIONS ? (
          <FormResponsesProvider
            formFileUploads={formFileUploads}
            setFormFileUploads={setFormFileUploads}
            formErrorIds={formErrorIds}
            setFormErrorIds={setFormErrorIds}
          >
            <SubscribeFlowQuestions
              creatorData={creatorData}
              profileQuestions={profileFormQuestions}
              processing={processing}
              answers={answers}
              setAnswers={setAnswers}
              verifyOnPress={submitQuestionsOnPress}
            />
          </FormResponsesProvider>
        ) : subscribingStage === SubscribingStage.MEMBERSHIP ? (
          <SubscribeFlowMembership
            creatorData={creatorData}
            chooseOnPress={chooseMembershipOnPress}
            selectedMembershipPlan={selectedMembershipPlan}
            setSelectedMembershipPlan={setSelectedMembershipPlan}
            visibleMembershipPlans={visibleMembershipPlans}
          />
        ) : subscribingStage === SubscribingStage.MEMBERSHIP_CHECKOUT &&
          selectedMembershipPlan ? (
          <SubscribeFlowMembershipCheckout
            creatorData={creatorData}
            selectedMembershipPlan={selectedMembershipPlan}
            uid={uid}
            backOnPress={() => setSubscribingStage(SubscribingStage.MEMBERSHIP)}
          />
        ) : null}
        <div
          className="Centering"
          style={{ paddingTop: 100, paddingBottom: 30 }}
        >
          <MarkitSecuredContainer />
        </div>
      </div>
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </>
  );
});

export default ProfilePageSubscribe;
